// Boss Fights (ADR-0025) admin list. Mirrors the adventures list:
// team + status filters, table of bosses, "New boss" CTA.

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../lib/api';
import { bossesApi } from './api';
import type { Boss, BossStatus } from './types';

const STATUS_OPTIONS: BossStatus[] = ['draft', 'active', 'vanquished', 'survived', 'archived'];

const STATUS_BADGE: Record<BossStatus, string> = {
  draft: 'bg-slate-700/40 text-slate-300 border-slate-600',
  active: 'bg-red-800/40 text-red-200 border-red-700',
  vanquished: 'bg-emerald-700/40 text-emerald-200 border-emerald-600',
  survived: 'bg-amber-700/40 text-amber-200 border-amber-600',
  archived: 'bg-zinc-700/40 text-zinc-400 border-zinc-600',
};

function StatusBadge({ status }: { status: BossStatus }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${STATUS_BADGE[status]}`}>
      {status}
    </span>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function BossesListPage() {
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams'),
  });
  const [filterTeam, setFilterTeam] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const bosses = useQuery({
    queryKey: ['admin-bosses', filterTeam, filterStatus],
    queryFn: () => bossesApi.list({
      team_id: filterTeam || undefined,
      status: (filterStatus || undefined) as BossStatus | undefined,
    }),
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Boss Fights</h1>
          <p className="text-sm text-ink-muted mt-1">
            Major initiatives (Jira epics). The team deals damage via activity; vanquish the boss before the window ends.
          </p>
        </div>
        <Link to="/admin/bosses/new" className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark transition">
          New boss
        </Link>
      </div>

      <div className="flex gap-3 mb-4">
        <select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="px-3 py-2 bg-bg-elev border border-border rounded text-sm">
          <option value="">All teams</option>
          {(teams.data?.teams ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 bg-bg-elev border border-border rounded text-sm">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-bg-elev border border-border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-ink-muted text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3">Epic</th>
              <th className="text-left px-4 py-3">Window</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {bosses.isPending && <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>}
            {bosses.isError && <tr><td colSpan={5} className="px-4 py-6 text-center text-red-400">Failed to load bosses.</td></tr>}
            {bosses.data?.bosses.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-muted">No bosses yet. Click "New boss" to author the first one.</td></tr>
            )}
            {bosses.data?.bosses.map((b: Boss) => (
              <tr key={b.id} className="border-t border-border hover:bg-bg-base/40">
                <td className="px-4 py-3">
                  <Link to={`/admin/bosses/${b.id}`} className="text-brand hover:underline">{b.name}</Link>
                </td>
                <td className="px-4 py-3 text-ink-muted">
                  {(teams.data?.teams ?? []).find((t) => t.id === b.team_id)?.name ?? b.team_id}
                </td>
                <td className="px-4 py-3 text-ink-muted font-mono text-xs">{b.jira_epic_key ?? '—'}</td>
                <td className="px-4 py-3 text-ink-muted text-xs">{fmt(b.starts_at)} → {fmt(b.ends_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
